import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

// Utils
import constants from "#src/config/constants";
import { createToast } from "#src/common/system/toasts";

// Assets
import { ReactComponent as GoogleLogomark } from "#src/assets/images/logos/socials/google-logomark.svg";

// API related
import directoryService from "#src/services/endpoint/directory";
import { apiErrorHandler } from "#src/utils/apiErrorHandler";
import { SYNC_DIRECTORY_WORKSPACE_SESSION_STORAGE_KEY } from "#src/services/endpoint/directory/types";

// Components
import { ButtonV2 } from "#src/commonV2/Button";

type Props = {
  workspaceId: string;
  onSuccess: () => void;
};

export const SyncWithGoogleBtn: React.FC<Props> = (props) => {
  const { workspaceId, onSuccess } = props;
  const { t } = useTranslation("endpoint", { keyPrefix: "directory" });
  const [searchParams] = useSearchParams();

  // ------------------- DATA -------------------

  const [isLoading, setIsLoading] = useState(false);
  const isLoaded = useRef(false);

  // ------------------- METHODS -------------------

  const goToGoogleAuth = () => {
    const url = new URL("https://accounts.google.com/o/oauth2/auth");
    const searchParams = new URLSearchParams();
    searchParams.append("protocol", "oauth2");
    searchParams.append("client_id", constants.GOOGLE_CLIENT_ID);
    searchParams.append("redirect_uri", getRedirectUrl());
    searchParams.append("response_type", "code");
    searchParams.append(
      "scope",
      "https://www.googleapis.com/auth/admin.directory.user.readonly"
    );
    url.search = searchParams.toString();
    window.sessionStorage.setItem(
      SYNC_DIRECTORY_WORKSPACE_SESSION_STORAGE_KEY,
      workspaceId
    );
    window.open(url.toString(), "_self");
  };

  const syncWithGoogle = useCallback(
    async (code: string, wsId: string) => {
      setIsLoading(true);
      try {
        await directoryService.syncUsersWithGoogle(wsId, {
          code,
          redirect_uri: getRedirectUrl(),
        });
        createToast({
          type: "success",
          message: t("google_sync.notification.success"),
        });
        onSuccess();
      } catch (error) {
        try {
          apiErrorHandler(error, {
            toastMessage: t("google_sync.notification.error"),
          });
        } catch (e) {
          //
        }
      }
      window.sessionStorage.removeItem(
        SYNC_DIRECTORY_WORKSPACE_SESSION_STORAGE_KEY
      );
      setIsLoading(false);
    },
    [onSuccess, t]
  );

  // ------------------- EFFECTS -------------------

  useEffect(() => {
    const code = searchParams.get("code");
    if (code && workspaceId && !isLoaded.current) {
      isLoaded.current = true;
      syncWithGoogle(code, workspaceId);
    }
  }, [searchParams, syncWithGoogle, workspaceId]);

  // ------------------- RENDER -------------------

  return (
    <ButtonV2
      variant="primary"
      size="md"
      color="brand"
      onClick={goToGoogleAuth}
      pending={isLoading}
    >
      {!isLoading && <GoogleLogomark className="h-5 w-5" />}
      <p>{t("google_sync.btn")}</p>
    </ButtonV2>
  );
};

const getRedirectUrl = () => {
  const redirectUrl = `${window.location.origin}/google-callback`;
  return redirectUrl;
};
